import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrdenesServicio } from '@/hooks/useOrdenesServicio'
import type { PedidoVentaResumen } from '@/types'

interface Props {
  dark: boolean
  pedido: PedidoVentaResumen
  onClose: () => void
  /** Se llama al enviarse la solicitud, con el mensaje de confirmación del backend. */
  onSent: (mensaje: string) => void
}

const apiError = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return (e as Error)?.message ?? ''
}

/** Solicitud de cancelación de un pedido abierto. La cancelación real es un
 *  proceso interno de Dynamics: esto solo avisa a administración. */
export function SolicitarCancelacionModal({ dark, pedido, onClose, onSent }: Props) {
  const { t } = useTranslation()
  const { solicitarCancelacion } = useOrdenesServicio()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const enviar = async () => {
    setError(null)
    try {
      const mensaje = await solicitarCancelacion.mutateAsync({
        numero: pedido.numero,
        empresa: pedido.empresa,
        motivo: motivo.trim(),
      })
      onSent(mensaje)
    } catch (e) {
      setError(apiError(e) || t('ordenesServicio.cancelar.error', { numero: pedido.numero }))
    }
  }

  const card = dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
  const label = `block text-[11px] font-semibold mb-1 ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const input = `w-full px-3 py-2 rounded-lg border text-[13px] ${
    dark ? 'bg-white/5 border-white/15 text-slate-100' : 'bg-white border-slate-300 text-slate-800'
  }`
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const btnDanger = 'px-4 min-h-[40px] rounded-lg text-[13px] font-semibold bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 transition-colors shadow-sm'
  const btnGhost = `px-4 min-h-[40px] rounded-lg text-[13px] font-semibold border transition-colors ${
    dark ? 'border-white/15 text-slate-300 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-100'
  }`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${card}`}>
        <div className="px-5 pt-5 pb-1">
          <h2 className={`text-[15px] font-black tracking-tight ${mainTxt}`}>
            {t('ordenesServicio.cancelar.titulo', { numero: pedido.numero })}
          </h2>
          <p className={`mt-1 text-[12px] ${mutedTxt}`}>
            {t('ordenesServicio.cancelar.descripcion')}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className={`rounded-lg border px-3 py-2 text-[12px] ${
            dark ? 'border-white/10 bg-white/5 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
          }`}>
            <span className={`font-semibold ${mainTxt}`}>{pedido.nombreCliente || pedido.cliente}</span>
            {pedido.numeroPedidoCliente && (
              <span> · {t('ordenesServicio.form.numeroPedidoCliente')}: {pedido.numeroPedidoCliente}</span>
            )}
            {pedido.fechaEnvio && <span> · {t('ordenesServicio.form.fechaEnvio')}: {pedido.fechaEnvio}</span>}
          </div>
          <div>
            <label className={label}>{t('ordenesServicio.cancelar.motivoLabel')}</label>
            <textarea
              className={`${input} min-h-[72px]`}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('ordenesServicio.cancelar.motivoPh')}
              autoFocus
            />
          </div>
          {error && <p className="text-[12px] text-red-500">{error}</p>}
        </div>

        <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${
          dark ? 'border-white/10' : 'border-slate-200'
        }`}>
          <button type="button" onClick={onClose} className={btnGhost}>
            {t('ordenesServicio.form.cancelar')}
          </button>
          <button type="button" onClick={enviar} disabled={solicitarCancelacion.isPending} className={btnDanger}>
            {solicitarCancelacion.isPending ? '…' : t('ordenesServicio.cancelar.enviar')}
          </button>
        </div>
      </div>
    </div>
  )
}
